import React, { FC, useEffect, useState } from 'react';
import { PaymentGatewayConfig } from '../APIClient';
import { useConvention, useGlobalState, useUser } from '../utils';
import { LoadingWrapper } from '../utils/LoadingWrapper';
import { makeLazyComponent } from '../utils/lazy';
import { Loading } from './Loading';
import { FormAlert } from './formElements/models/FormAlert';
import { PaymentProps, PaymentState } from '.';

interface PaymentGateway {
  name: string;
  type: 'stripe' | 'stripeConnect';
  lazyComponent: FC<PaymentProps>;
}

export const paymentGateways: PaymentGateway[] = [
  {
    lazyComponent: makeLazyComponent(
      async () =>
        (await import(/* webpackChunkName: "stripepayment" */ './payment/StripePayment'))
          .StripePayment,
    ),
    name: 'Stripe',
    type: 'stripeConnect',
  },
  {
    lazyComponent: makeLazyComponent(
      async () =>
        (await import(/* webpackChunkName: "stripepayment" */ './payment/StripePayment'))
          .StripePayment,
    ),
    name: 'Stripe',
    type: 'stripe',
  },
];

interface PaymentResult {
  state: PaymentState;
  message?: string;
}

interface WrapperProps {
  readonly total: number;
  readonly onSuccessTimeout?: number;
  onSuccess(): void;
}

interface Props extends WrapperProps {
  readonly gatewayConfig: PaymentGatewayConfig;
}

interface ErrorProps {
  readonly error?: string;
}

const PaymentGatewayError: FC<ErrorProps> = ({ error }) => {
  const convention = useConvention();

  return (
    <div className="payment-gateway payment-gateway-error">
      <p>Sorry, we can't accept payments right now.</p>
      <p>Please contact registration@{convention.domain} for assistance.</p>
      {error && (
        <p className="text-muted">
          <small>
            <strong>Error</strong>: {error}
          </small>
        </p>
      )}
    </div>
  );
};

export const PaymentGatewayWrapper: FC<WrapperProps> = (props) => {
  return (
    <LoadingWrapper
      dataFetcher={async () => await api.getPaymentGateways()}
      errorDisplay={(err) => <PaymentGatewayError error={err.message} />}
    >
      {(data) => <PaymentGatewayComponent {...props} gatewayConfig={data} />}
    </LoadingWrapper>
  );
};

const PaymentGatewayComponent: FC<Props> = ({
  gatewayConfig,
  total,
  onSuccess,
  onSuccessTimeout = 0,
}) => {
  const [paymentState, setPaymentState] = useState<PaymentResult>({ state: PaymentState.Pending });
  const user = useUser();
  const convention = useConvention();
  const contacts = useGlobalState((s) => s.config?.contact);

  useEffect(() => {
    if (paymentState.state === PaymentState.Success) {
      setTimeout(onSuccess, onSuccessTimeout);
    }
  }, [paymentState, onSuccess, onSuccessTimeout]);

  // The kiosk mode timeout may log out the user.
  if (!user) {
    return <PaymentGatewayError error="User is not logged in." />;
  }

  if (paymentState.state === PaymentState.Success) {
    return (
      <div className="payment-gateway">
        <FormAlert markdown={paymentState.message!} type="success" />
      </div>
    );
  }

  for (const gateway of paymentGateways) {
    if (gateway.type === gatewayConfig.type) {
      return (
        <div className="payment-gateway">
          <h4>Pay by Credit / Debit Card</h4>
          <p>
            <small>Processing by {gateway.name}</small>
          </p>
          {paymentState.state === PaymentState.Processing && (
            <Loading>
              <p>Processing Payment...</p>
            </Loading>
          )}
          <hr />
          <div className="payment-gateway-form">
            {paymentState.state === PaymentState.Failed && (
              <FormAlert
                markdown={
                  paymentState.message ??
                  `An unexpected error occurred. Please contact ${
                    contacts?.email.registration ?? 'the registration team'
                  } for assistance.`
                }
                type="error"
              />
            )}
            <gateway.lazyComponent
              conventionName={convention.longName}
              onUpdate={(state, message) => {
                setPaymentState({ state, message });
              }}
              paymentConfig={gatewayConfig}
              total={total}
              user={user}
            />
          </div>
        </div>
      );
    }
  }

  return <PaymentGatewayError error="No payment gateways have been configured." />;
};
